import { useId, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import clsx from 'clsx';

/**
 * A small accessible info tooltip for section headings across reporting and the
 * back-office finance screens. Renders an info glyph that reveals an explanation
 * on hover AND keyboard focus (focus-within), so a reader can learn what a metric
 * is and how it is calculated without leaving the page.
 *
 * Light-theme first (this project designs in light only); colours come from the
 * semantic tokens so it stays theme-aware. Pure CSS show/hide - no JS state - and
 * the popover is `role="tooltip"` linked via `aria-describedby`.
 */
interface Props {
  /** The explanation shown on hover/focus. Keep it to what-it-is + how-it-is-calculated. */
  children: ReactNode;
  /** Accessible label for the trigger; defaults to a generic "More information". */
  label?: string;
  /** Align the popover to the right edge of the trigger instead of the left (for headings near the right edge). */
  align?: 'left' | 'right';
  className?: string;
}

export default function InfoTip({ children, label = 'More information', align = 'left', className }: Props) {
  const id = useId();
  return (
    <span className={clsx('relative inline-flex group align-middle', className)}>
      <button
        type="button"
        aria-label={label}
        aria-describedby={id}
        className="inline-flex items-center justify-center text-cream-faint hover:text-gold-bright focus:text-gold-bright focus:outline-none transition-colors"
      >
        <Info size={13} strokeWidth={2} aria-hidden="true" />
      </button>
      <span
        role="tooltip"
        id={id}
        className={clsx(
          'pointer-events-none absolute top-full mt-1.5 z-50 w-64 rounded-md border border-gold-dim/40 bg-ink-soft/95 px-3 py-2',
          'font-inter text-[11px] leading-snug text-cream-soft normal-case tracking-normal shadow-lg',
          'opacity-0 translate-y-0.5 transition-all duration-100',
          'group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0',
          align === 'right' ? 'right-0' : 'left-0',
        )}
      >
        {children}
      </span>
    </span>
  );
}
